import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { CurrentUser } from '../api';
import { NhcLogo } from './NhcLogo';
import { MicroTechCredit } from './MicroTechCredit';
import { LegalNoticeButton } from './LegalNotice';
import {
  IconArchive,
  IconCheckCircle,
  IconClipboard,
  IconClose,
  IconCog,
  IconHome,
  IconServer,
  IconShield,
  IconSliders,
  IconStar,
  IconUsers,
} from './icons';
import { canWorkItQueue, isAdmin, isApprovedViewer, isApprover, isSystemAdmin } from '../roles';

type Item = { to: string; label: string; icon: React.ReactNode };

/**
 * Navigation down the side rather than across the top.
 *
 * The bar had run out of room: seven destinations, an identity, a bell and a
 * theme switch on one line, with the navigation scrolling to make space and
 * "All requests" clipped to "All". A column does not have that problem: it
 * grows downwards, every label fits at full length beside its icon, and the
 * administrator pages can sit in a titled group instead of hiding behind a
 * dropdown. It also leaves the top of the page to the page itself.
 *
 * Below `lg` it is a drawer: the same markup, slid off-canvas and brought back
 * by the button in the top bar.
 */
export function Sidebar({
  user,
  open,
  onClose,
}: {
  user: CurrentUser;
  open: boolean;
  onClose: () => void;
}) {
  const { pathname } = useLocation();

  // Navigating is what closes the drawer: leaving it over the page you just
  // asked for is the classic way to make a mobile menu feel broken.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // The viewer gets one destination. Not the dashboard, not the archive:
  // both would be mostly things that role cannot open.
  const main: Item[] = isApprovedViewer(user.role)
    ? [{ to: '/', label: 'Approved requests', icon: <IconCheckCircle className="h-[18px] w-[18px]" /> }]
    : [
        { to: '/', label: 'Dashboard', icon: <IconHome className="h-[18px] w-[18px]" /> },
        { to: '/requests', label: 'My requests', icon: <IconClipboard className="h-[18px] w-[18px]" /> },
        ...(isApprover(user.role)
          ? [{ to: '/queue', label: 'Approval queue', icon: <IconCheckCircle className="h-[18px] w-[18px]" /> }]
          : []),
        { to: '/archive', label: 'All requests', icon: <IconArchive className="h-[18px] w-[18px]" /> },
      ];

  const itQueueItem: Item = { to: '/it-queue', label: 'IT queue', icon: <IconServer className="h-[18px] w-[18px]" /> };

  const admin: Item[] = [
    { to: '/admin/users', label: 'Users & roles', icon: <IconUsers className="h-[18px] w-[18px]" /> },
    { to: '/admin/settings', label: 'Form settings', icon: <IconSliders className="h-[18px] w-[18px]" /> },
    // System settings and the audit log are for Fred and Sven alone, not
    // every administrator tier, so they are kept out of this list for anyone else.
    ...(isSystemAdmin(user.role)
      ? [
          { to: '/admin/system', label: 'System settings', icon: <IconCog className="h-[18px] w-[18px]" /> },
          { to: '/admin/audit', label: 'Audit log', icon: <IconShield className="h-[18px] w-[18px]" /> },
          { to: '/admin/feedback', label: 'Feedback', icon: <IconStar className="h-[18px] w-[18px]" /> },
        ]
      : []),
  ];

  return (
    <>
      {/* Backdrop, drawer only. */}
      <div
        className={`animate-fade fixed inset-0 z-30 bg-slate-950/40 backdrop-blur-[2px] lg:hidden ${open ? '' : 'hidden'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        id="app-sidebar"
        aria-label="Sections"
        className={
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white ' +
          'transition-transform duration-200 ease-out lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 ' +
          'dark:border-slate-800 dark:bg-slate-950 ' +
          (open ? 'translate-x-0 shadow-2xl' : '-translate-x-full')
        }
      >
        <div className="flex min-h-16 shrink-0 items-center gap-2.5 px-4 py-2.5">
          <Link
            to="/"
            className="group flex min-w-0 items-center gap-2.5"
            aria-label="New Horizon Copper Enrolment Program, home"
          >
            <NhcLogo className="h-9 w-auto shrink-0 transition-transform duration-200 group-hover:scale-105" />
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13px] font-semibold tracking-tight text-slate-800 dark:text-slate-100">
                New Horizon Copper
              </span>
              <span className="block truncate text-[13px] font-semibold tracking-tight text-slate-800 dark:text-slate-100">
                Enrolment Program
              </span>
            </span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="ml-auto rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 lg:hidden dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          <ul className="space-y-0.5">
            {main.map((item) => (
              <NavRow key={item.to} item={item} pathname={pathname} />
            ))}
          </ul>

          {isAdmin(user.role) ? (
            <>
              <p className="mt-6 mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                Administration
              </p>
              <ul className="space-y-0.5">
                {[itQueueItem, ...admin].map((item) => (
                  <NavRow key={item.to} item={item} pathname={pathname} />
                ))}
              </ul>
            </>
          ) : (
            // An MTN Technician gets the queue on its own, not the
            // "Administration" heading: everything else under it (users,
            // form settings, system settings) is still out of reach.
            canWorkItQueue(user.role) && (
              <>
                <p className="mt-6 mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
                  IT
                </p>
                <ul className="space-y-0.5">
                  <NavRow item={itQueueItem} pathname={pathname} />
                </ul>
              </>
            )
          )}
        </nav>

        <div className="shrink-0 border-t border-slate-200 px-4 py-3.5 text-xs dark:border-slate-800">
          <LegalNoticeButton />
          <MicroTechCredit stacked />
        </div>
      </aside>
    </>
  );
}

function NavRow({ item, pathname }: { item: Item; pathname: string }) {
  const current = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
  return (
    <li>
      <Link
        to={item.to}
        aria-current={current ? 'page' : undefined}
        className={
          'press group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ' +
          (current
            ? 'bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/40 dark:text-brand-100'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white')
        }
      >
        {/* The marker rides the left edge, where a column reads its own state.
            Grown into place rather than just faded, so arriving at a
            destination reads as landing on it. */}
        <span
          aria-hidden="true"
          className={
            'absolute top-1.5 bottom-1.5 -left-2 w-0.5 origin-center rounded-full bg-brand-600 transition-all duration-200 dark:bg-brand-400 ' +
            (current ? 'opacity-100' : 'opacity-0')
          }
        />
        <span className={current ? 'text-brand-700 dark:text-brand-300' : 'text-slate-500 dark:text-slate-400'}>
          {item.icon}
        </span>
        {item.label}
      </Link>
    </li>
  );
}
